import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Send, Loader2, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { RichTextEditor, sanitizeRichHtml } from '../../components/RichTextEditor';
import { UserMultiPicker } from './UserMultiPicker';
import { ResourcePicker } from './ResourcePicker';

const GroupChips = ({ groups, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2">
    {groups.length === 0 && <span className="text-xs text-slate-500">Nessun gruppo: creane uno nel tab Gruppi.</span>}
    {groups.map((g) => (
      <button
        type="button" key={g.id} onClick={() => onToggle(g.id)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selected.includes(g.id) ? 'bg-amber-500 border-amber-400 text-slate-950' : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-500'}`}
        data-testid={`bc-group-${g.id}`}
      >
        {g.name} · {g.member_count}
      </button>
    ))}
  </div>
);

export const BroadcastModal = ({ open, onClose, preset, groups, users, backendUrl, token, showToast, onSent }) => {
  const [groupIds, setGroupIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [html, setHtml] = useState('');
  const [plain, setPlain] = useState('');
  const [resource, setResource] = useState(null);
  const [grantAccess, setGrantAccess] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGroupIds(preset?.groupIds || []);
    setUserIds(preset?.userIds || []);
    setHtml(''); setPlain(''); setResource(null); setGrantAccess(true);
  }, [open, preset]);

  const recipientIds = new Set(userIds);
  groups.filter((g) => groupIds.includes(g.id)).forEach((g) => g.member_ids.forEach((id) => recipientIds.add(id)));
  const toggleGroup = (id) => setGroupIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const send = async () => {
    setSending(true);
    try {
      const hasFormatting = /<(strong|em|u|a|ul|ol|li|h3|h4|br)\b/i.test(html) || /<p>[^<]*<\/p>\s*<p>/i.test(html);
      const res = await axios.post(`${backendUrl}/api/admin/messages/broadcast`, {
        user_ids: userIds,
        group_ids: groupIds,
        content: plain,
        content_html: hasFormatting ? sanitizeRichHtml(html) : '',
        resource: resource ? { kind: resource.kind, id: resource.id } : null,
        grant_access: grantAccess,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const { sent, lead_count: leads } = res.data;
      showToast('success', `Inviato a ${sent} destinatari${leads ? ` (${leads} lead: solo email)` : ''}`);
      onSent?.();
      onClose();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Errore invio');
    } finally {
      setSending(false);
    }
  };

  const canSend = recipientIds.size > 0 && (plain.trim() || resource) && !sending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-slate-800 border-slate-700 text-white max-h-[92vh] overflow-y-auto" data-testid="broadcast-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-amber-400" /> Invio a gruppi e utenti</DialogTitle>
          <DialogDescription className="text-slate-400">Ogni destinatario riceve il messaggio nella sua chat privata e via email.</DialogDescription>
        </DialogHeader>
        <section className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-slate-400">Gruppi</p>
          <GroupChips groups={groups} selected={groupIds} onToggle={toggleGroup} />
        </section>
        <section className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-slate-400">Utenti singoli (opzionale)</p>
          <UserMultiPicker users={users} selected={userIds} onChange={setUserIds} testIdPrefix="bc-users" />
        </section>
        <section className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-slate-400">Messaggio</p>
          <RichTextEditor value={html} onChange={setHtml} onPlainTextChange={setPlain} placeholder="Scrivi il messaggio per tutti i destinatari…" minHeight={110} />
        </section>
        <section className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-slate-400">Collega una risorsa (opzionale)</p>
          <ResourcePicker backendUrl={backendUrl} token={token} value={resource} onChange={setResource} testIdPrefix="bc-res" />
          {resource && !resource.is_public && (
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={grantAccess} onChange={(e) => setGrantAccess(e.target.checked)} className="accent-amber-500" data-testid="bc-grant-access" />
              Risorsa riservata: concedi l'accesso automaticamente ai destinatari
            </label>
          )}
        </section>
        <div className="flex items-center justify-between pt-2 border-t border-slate-700">
          <span className="text-sm text-slate-300" data-testid="bc-recipient-count">{recipientIds.size} destinatari</span>
          <Button onClick={send} disabled={!canSend} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="bc-send-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Invia a tutti
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
